import { FadeUp } from './Reveal';

const Chapter = ({ n, label }) => (
  <div className="flex items-baseline gap-4 mb-10" data-testid={`chapter-${n}`}>
    <span className="font-serif italic text-white/40 text-4xl md:text-5xl font-light leading-none">{n}</span>
    <span className="font-mono text-[10px] md:text-[11px] uppercase tracking-[0.3em] text-white/50">— {label}</span>
  </div>
);

export function Bottleneck({ body }) {
  return (
    <section className="relative bg-ink-950 py-24 md:py-40 border-t border-white/10" data-testid="section-bottleneck">
      <div className="mx-auto max-w-[1600px] px-6 md:px-10 grid grid-cols-12 gap-6 md:gap-12">
        <div className="col-span-12 lg:col-span-4">
          <Chapter n="01" label="The Bottleneck" />
          <FadeUp>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-white font-light leading-[1] tracking-tight">
              Italian bureaucracy is friction, not a wall.
            </h2>
          </FadeUp>
        </div>
        <div className="col-span-12 lg:col-span-7 lg:col-start-6 lg:pt-20">
          <FadeUp delay={0.1}>
            <p className="text-lg md:text-xl text-white/70 leading-relaxed">{body}</p>
          </FadeUp>
          <FadeUp delay={0.2} className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-6 border-t border-white/10 pt-10">
            {[
              ['Local', 'Filed and enforced in Italy.'],
              ['Continuity', 'Handled by counsel of record.'],
              ['Reachable', 'Verona · acting throughout Italy.'],
            ].map(([k, v]) => (
              <div key={k}>
                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/40 mb-2">— {k}</div>
                <div className="font-serif text-xl text-white leading-snug">{v}</div>
              </div>
            ))}
          </FadeUp>
        </div>
      </div>
    </section>
  );
}

const STEPS = [
  {
    n: '01',
    title: 'Preliminary claim assessment',
    body: 'You submit your contract, invoices and correspondence through the secure form. We review the documents and provide an initial written assessment — procedural route, expected timing and cost estimate — at no cost.',
  },
  {
    n: '02',
    title: 'Formal demand',
    body: 'A demand letter under Italian legal standards, sent by PEC or registered post to the debtor, aimed at obtaining voluntary payment before any litigation.',
  },
  {
    n: '03',
    title: 'Local action & enforcement',
    body: 'Where the debtor refuses to engage, we file the appropriate proceeding — decreto ingiuntivo, ordinary claim, and enforcement (esecuzione forzata) — before the competent Italian tribunal.',
  },
];

export function Workflow() {
  return (
    <section id="workflow" className="relative bg-ink-900 py-24 md:py-40 border-t border-white/10" data-testid="section-workflow">
      <div className="mx-auto max-w-[1600px] px-6 md:px-10">
        <div className="grid grid-cols-12 gap-6 md:gap-12 mb-16 md:mb-24">
          <div className="col-span-12 lg:col-span-5">
            <Chapter n="02" label="Our Workflow" />
            <FadeUp>
              <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-white font-light leading-[1] tracking-tight">
                Four disciplined <br /><em className="not-italic text-white/50">steps.</em>
              </h2>
            </FadeUp>
          </div>
          <div className="col-span-12 lg:col-span-6 lg:col-start-7 lg:pt-16">
            <FadeUp delay={0.1}>
              <p className="text-lg text-white/70 leading-relaxed">
                Each file is prepared to the standard Italian tribunals expect, and pursued locally by
                counsel of record. Every step is delivered as a written, timestamped record —
                easy to share with your board and in-house counsel.
              </p>
            </FadeUp>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10">
          {STEPS.map((s, i) => (
            <FadeUp key={s.n} delay={i * 0.06}>
              <div className="bg-ink-900 h-full p-8 lg:p-10 relative min-h-[280px] group">
                <div className="flex items-baseline justify-between mb-8">
                  <span className="font-serif italic text-6xl md:text-7xl font-light text-white/25 group-hover:text-oxblood transition-colors duration-500">
                    {s.n}
                  </span>
                  <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/40">Step</span>
                </div>
                <h3 className="font-serif text-2xl text-white leading-tight mb-4">{s.title}</h3>
                <p className="text-sm text-white/60 leading-relaxed">{s.body}</p>
              </div>
            </FadeUp>
          ))}
        </div>
      </div>
    </section>
  );
}

/**
 * Manifesto — the "Italian on your side" moment.
 * Editorial, generous, centred rhythm. This is the emotional pivot of each country page.
 */
export function Manifesto() {
  return (
    <section
      className="relative bg-ink-950 py-24 md:py-40 border-t border-white/10 overflow-hidden"
      data-testid="section-manifesto"
    >
      <div className="mx-auto max-w-[1400px] px-6 md:px-10 grid grid-cols-12 gap-6 md:gap-12">
        <div className="col-span-12 lg:col-span-5 relative min-h-[400px] lg:min-h-[600px]">
          <div className="absolute inset-0 clip-frame-tr overflow-hidden">
            <img
              src="/christian-belliardo.jpg"
              alt="Studio Legale Belliardo — Verona"
              className="w-full h-full object-cover object-[center_15%] grayscale contrast-105 brightness-95"
            />
            <div className="absolute inset-0 bg-gradient-to-br from-ink-950/30 via-transparent to-ink-950/80" />
          </div>
          <div className="absolute -bottom-4 -left-4 hidden lg:flex items-center gap-3 bg-ink-950 px-4 py-2 border border-white/10">
            <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/60">— Studio Legale Belliardo · Verona</span>
          </div>
        </div>

        <div className="col-span-12 lg:col-span-7 lg:col-start-6 flex flex-col justify-center">
          <Chapter n="03" label="Why an Italian lawyer" />
          <FadeUp>
            <h2 className="font-serif text-4xl md:text-6xl lg:text-7xl text-white font-light leading-[1.02] tracking-tight">
              We know Italy. <br />
              <em className="not-italic text-white/50">We are on your side.</em>
            </h2>
          </FadeUp>
          <FadeUp delay={0.1}>
            <p className="mt-8 font-serif italic text-xl md:text-2xl text-white/70 leading-snug max-w-xl">
              To deal with Italy, it helps to have an Italian on your side.
            </p>
          </FadeUp>
          <FadeUp delay={0.2} className="mt-10 max-w-xl">
            <p className="text-lg text-white/70 leading-relaxed">
              Italian courts, enforcement procedures and administrative formalities operate within
              a procedural framework that is unfamiliar to many foreign counsel. We practise
              Italian law here, every day. That familiarity — with the courthouse, with local
              procedure, with local court practice — is the difference between a file that
              moves and a file that stalls.
            </p>
          </FadeUp>
          <FadeUp delay={0.25} className="mt-8 max-w-xl border-l-2 border-oxblood pl-5 py-1">
            <p className="text-sm md:text-base text-white/60 leading-relaxed">
              <span className="text-white/85 font-medium">Filed nationwide.</span>{' '}
              Verona is our base. Italy&apos;s electronic civil procedure (<em className="not-italic text-white/85">Processo Civile Telematico</em>){' '}
              allows us to file, receive service and correspond with any Italian tribunal remotely.
            </p>
          </FadeUp>
          <FadeUp delay={0.3} className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-6 border-t border-white/10 pt-10 max-w-2xl">
            {[
              ['One point of contact', 'in Verona · accountable for the file'],
              ['48h', 'written reply on working days'],
              ['Video calls', 'in English, when useful'],
            ].map(([k, v]) => (
              <div key={k}>
                <div className="font-serif text-xl text-white leading-snug">{k}</div>
                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/40 mt-2">{v}</div>
              </div>
            ))}
          </FadeUp>
        </div>
      </div>
    </section>
  );
}

/**
 * Confidentiality — segreto professionale reassurance. Placed near the form
 * to lower the trust barrier when a foreign creditor is about to send confidential
 * commercial documents.
 */
export function Confidentiality() {
  return (
    <section
      className="relative bg-ink-900 py-20 md:py-28 border-t border-white/10"
      data-testid="section-confidentiality"
    >
      <div className="mx-auto max-w-[1400px] px-6 md:px-10 grid grid-cols-12 gap-6 md:gap-12">
        <div className="col-span-12 lg:col-span-5">
          <Chapter n="04" label="Professional secrecy" />
          <FadeUp>
            <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl text-white font-light leading-tight tracking-tight">
              <em className="not-italic text-white/50">Professional secrecy</em> under Italian law.
            </h2>
          </FadeUp>
        </div>
        <div className="col-span-12 lg:col-span-6 lg:col-start-7 lg:pt-8">
          <FadeUp delay={0.1}>
            <p className="text-lg text-white/70 leading-relaxed">
              Information and documents received in connection with the professional relationship are
              subject to the duties of <em className="not-italic text-white/85">segreto professionale</em>{' '}
              and confidentiality imposed on Italian lawyers by art. 622 of the Italian Criminal Code
              and art. 28 of the Codice Deontologico Forense.
            </p>
          </FadeUp>
          <FadeUp delay={0.2} className="mt-8 divide-y divide-white/10 border-y border-white/10">
            {[
              ['Professional secrecy', 'Italian lawyers are bound by professional secrecy — a duty backed by criminal law and by binding professional rules.'],
              ['No unauthorised sharing', 'Documents are not forwarded to third parties outside what the professional relationship requires and applicable rules allow.'],
              ['Data protection', 'Personal data is processed in accordance with the GDPR; retention is limited to what applicable law permits.'],
              ['Reversibility', 'If you decide not to proceed, files are returned or destroyed on request, subject to statutory retention duties (e.g. anti-money-laundering).'],
            ].map(([k, v], i) => (
              <div key={k} className="py-4 grid grid-cols-12 gap-4" data-testid={`confidentiality-${i}`}>
                <div className="col-span-12 md:col-span-4">
                  <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50">— {k}</span>
                </div>
                <div className="col-span-12 md:col-span-8 text-white/70 leading-relaxed text-sm">{v}</div>
              </div>
            ))}
          </FadeUp>
          <FadeUp delay={0.3}>
            <div className="mt-6 font-mono text-[10px] uppercase tracking-[0.3em] text-white/40">
              — Italian professional secrecy · art. 622 c.p. + art. 28 Codice Deontologico Forense
            </div>
          </FadeUp>
        </div>
      </div>
    </section>
  );
}

export function TrustSignals({ trust, accent }) {
  return (
    <section className="relative bg-ink-950 py-24 md:py-40 border-t border-white/10" data-testid="section-trust">
      <div className="mx-auto max-w-[1600px] px-6 md:px-10 grid grid-cols-12 gap-6 md:gap-12">
        <div className="col-span-12 lg:col-span-5">
          <Chapter n="05" label="Why work with our practice" />
          <FadeUp>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-white font-light leading-[1] tracking-tight">
              {trust.title}
            </h2>
          </FadeUp>
          <div className="mt-6 h-px w-24" style={{ background: accent }} aria-hidden />
        </div>
        <div className="col-span-12 lg:col-span-6 lg:col-start-7 lg:pt-16">
          <FadeUp delay={0.1}>
            <p className="text-lg md:text-xl text-white/70 leading-relaxed">{trust.body}</p>
          </FadeUp>
          <FadeUp delay={0.2} className="mt-10 divide-y divide-white/10 border-y border-white/10">
            {trust.bullets.map((b, i) => (
              <div key={i} className="py-5 flex items-baseline gap-6" data-testid={`trust-bullet-${i}`}>
                <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/40 w-8">
                  0{i + 1}
                </span>
                <span className="font-serif text-xl md:text-2xl text-white leading-snug">{b}</span>
              </div>
            ))}
          </FadeUp>
        </div>
      </div>
    </section>
  );
}

// Keep InternationalDesk exported as a thin alias to Manifesto for backwards compat.
export const InternationalDesk = Manifesto;

/**
 * ForeignLawyers — B2L section aimed at foreign counsel who need an Italian
 * correspondent. Includes discreet BNI referral-network mention.
 */
export function ForeignLawyers() {
  const services = [
    'Italian litigation',
    'Debt recovery',
    'Enforcement (esecuzione forzata)',
    'Service of documents in Italy',
    'Italian debtor investigations',
    'Coordination with foreign counsel',
  ];
  return (
    <section
      className="relative bg-ink-950 py-24 md:py-32 border-t border-white/10"
      data-testid="section-foreign-lawyers"
    >
      <div className="mx-auto max-w-[1400px] px-6 md:px-10 grid grid-cols-12 gap-6 md:gap-12">
        <div className="col-span-12 lg:col-span-5">
          <Chapter n="06" label="For foreign lawyers" />
          <FadeUp>
            <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl text-white font-light leading-tight tracking-tight">
              Need an Italian legal partner <br />
              <em className="not-italic text-white/50">for your client?</em>
            </h2>
          </FadeUp>
          <FadeUp delay={0.1}>
            <p className="mt-6 text-lg text-white/70 leading-relaxed max-w-md">
              Your client has a claim against an Italian debtor. We act as your Italian legal partner —
              handling proceedings and enforcement locally in Italy, and keeping you informed at every stage.
            </p>
          </FadeUp>
          <FadeUp delay={0.2}>
            <a
              href="/for-lawyers"
              data-testid="foreign-lawyers-cta"
              className="mt-8 inline-flex items-center gap-3 border border-white/25 px-5 py-3 text-[11px] font-mono uppercase tracking-[0.25em] text-white hover:bg-white hover:text-black transition-colors duration-300"
            >
              How we work with instructing counsel →
            </a>
          </FadeUp>
        </div>
        <div className="col-span-12 lg:col-span-6 lg:col-start-7 lg:pt-8">
          <FadeUp delay={0.15}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
              {services.map((s, i) => (
                <div key={s} className="flex items-baseline gap-3" data-testid={`fl-service-${i}`}>
                  <span className="font-mono text-[10px] text-white/40 w-6">0{i + 1}</span>
                  <span className="font-serif text-lg text-white/85 leading-snug">{s}</span>
                </div>
              ))}
            </div>
          </FadeUp>
          <FadeUp delay={0.3}>
            <div className="mt-10 pt-8 border-t border-white/10 flex items-baseline gap-4 flex-wrap" data-testid="bni-membership">
              <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50">— Referral network</span>
              <div>
                <div className="font-serif text-lg text-white leading-snug">BNI Member</div>
                <div className="text-white/50 text-sm mt-1">
                  Member of BNI, the international business referral network.
                </div>
              </div>
            </div>
          </FadeUp>
        </div>
      </div>
    </section>
  );
}
